import React from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { CustomerAccountNavigation } from '@thd-olt-component-react/customer-account-navigation';
import { QueryProvider } from '@thd-nucleus/data-sources';
import {
  Alert,
  Tab, TabController, TabList, TabPanel, Typography
} from '@one-thd/sui-atomic-components';
import InfoBox from './components/InfoBox';
import EmailPreferences from './components/EmailPreferences';
import SmsPreferences from './components/SmsPreferences';
import TelemarketingPreferences from './components/TelemarketingPreferences';
import MailingPreferences from './components/MailingPreferences';
import LanguagePreference from './components/LanguagePreference';
import Header from './components/Header';
import DisclaimerBox from './components/DisclaimerBox';
import { setSourceId } from './apis/common';
import { UserDataContext, UserDataProvider } from './contexts/UserDataProvider';
import SurveyEmailPreference from './components/SurveyEmailPreference';
import ProXtraPreferences from './components/ProXtraPreferences';
import { isB2CUser } from '../utils/general';
import { SurveyFeedback } from './components/SurveyFeedback';
import OrderPreferences from './components/OrderPreferences';
import { RetrieveErrorContext, RetrieveErrorProvider } from './contexts/RetrieveErrorContext';
import { ERROR_RETRIEVING_PREFERENCES, YES } from './constants';
import SmsTelemarketingPreferences from './components/SmsTelemarketingPreferences';

export const preferencesTestId = 'communication-preferences-app';
export const communicationPrefsTestId = 'communication-preferences-app';
export const preferencesTestIdTab = 'communication-preferences-app-tab';

export const Preferences = () => {

  const [tabValue, setTabValue] = React.useState('Marketing Tab');
  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const { channel } = React.useContext(ExperienceContext);
  const {
    email,
    phoneNumber,
    addresses,
    managedAccount,
    isTabsEnabled,
    isOrderCommsEnabled,
    isEReceiptEnabled,
    isB2B,
    isFccEnabled,
    accountOwner
  } = React.useContext(UserDataContext);
  setSourceId(channel);

  const {
    hasError
  } = React.useContext(RetrieveErrorContext);
  const isProXtraEnabled = useConfigService('proxtra_component_enabled');
  const isLangPrefEnabled = useConfigService('langpref_component_enabled');

  function showProXtra() {
    return !isB2CUser() && isProXtraEnabled;
  }

  function showEReceipt() {
    return isEReceiptEnabled && !isB2CUser() && accountOwner === YES;
  }

  function showOrderCommunications() {
    return showEReceipt() || (isOrderCommsEnabled && (managedAccount === YES));
  }

  function multiTabView() {
    return isLangPrefEnabled || showOrderCommunications();
  }

  function shouldTabsRender() {
    return isTabsEnabled && multiTabView();
  }

  function reportErrorWarning() {
    return (
      <Alert status="warning" size="large">
        <Typography>{ERROR_RETRIEVING_PREFERENCES}</Typography>
      </Alert>
    );
  }

  function fccExperience() {
    return (
      <div className="sui-grid sui-gap-8">
        {showProXtra() && <ProXtraPreferences email={email} />}
        <EmailPreferences email={email} isB2B={isB2B} />
        <SurveyEmailPreference email={email} />
        <SmsTelemarketingPreferences phoneNumber={phoneNumber} isB2B={isB2B} />
        <MailingPreferences addresses={addresses} />
        <DisclaimerBox />
      </div>
    );
  }

  function tabStructureRender() {
    return (
      <div className="sui-col-span-12 lg:sui-col-span-8" data-testid={preferencesTestIdTab}>
        <div className="sui-grid sui-gap-8">
          <InfoBox email={email} phoneNumber={phoneNumber} tabValue={tabValue} />
          <TabController value={tabValue}>
            <TabList onChange={handleChange} aria-label="tabs context example" variant="scrollable">
              <Tab label="Marketing Communications" value="Marketing Tab" />
              {showOrderCommunications()
                && (
                  <Tab label="Order Communications" value="Order Tab" />
                )}
              {isLangPrefEnabled && (
                <Tab label="Personalization" value="Personalization Tab" />
              )}
            </TabList>
            {hasError && (reportErrorWarning())}
            <TabPanel className="!sui-p-0" value="Marketing Tab">
              <div className="sui-col-span-12 lg:sui-col-span-8">
                {isFccEnabled ? (fccExperience()) : (
                  <div className="sui-grid sui-gap-8">
                    {showProXtra() && <ProXtraPreferences email={email} />}
                    <EmailPreferences email={email} isB2B={isB2B} />
                    <SurveyEmailPreference email={email} />
                    <SmsPreferences phoneNumber={phoneNumber} isB2B={isB2B} />
                    <TelemarketingPreferences phoneNumber={phoneNumber} />
                    <MailingPreferences addresses={addresses} />
                    <DisclaimerBox />
                  </div>
                )}
              </div>
            </TabPanel>
            <TabPanel className="!sui-p-0" value="Order Tab">
              <div className="sui-col-span-12 lg:sui-col-span-8">
                <div className="sui-grid sui-gap-8">
                  <OrderPreferences
                    email={email}
                    phoneNumber={phoneNumber}
                    managedAccount={managedAccount}
                    accountOwner={accountOwner}
                    isEReceiptEnabled={isEReceiptEnabled}
                    isOrderCommsEnabled={isOrderCommsEnabled}
                  />
                </div>
              </div>
            </TabPanel>
            <TabPanel className="!sui-p-0" value="Personalization Tab">
              <div className="sui-col-span-12 lg:sui-col-span-8">
                <div className="sui-grid sui-gap-8">
                  {isProXtraEnabled && <ProXtraPreferences email={email} />}
                  <LanguagePreference email={email} />
                </div>
              </div>
            </TabPanel>
          </TabController>
        </div>
        <SurveyFeedback />
      </div>
    );
  }

  function defaultStructureRender() {
    return (
      <div className="sui-col-span-12 lg:sui-col-span-8" data-testid={preferencesTestId}>
        <div className="sui-grid sui-gap-8">
          <InfoBox email={email} phoneNumber={phoneNumber} />
          {hasError && (reportErrorWarning())}
          {isFccEnabled ? (fccExperience()) : (
            <div className="sui-grid sui-gap-8 sm:sui-gap-16 md:sui-pt-8">
              {!isB2CUser() && isProXtraEnabled && <ProXtraPreferences email={email} />}
              <EmailPreferences email={email} isB2B={isB2B} />
              <SurveyEmailPreference email={email} />
              <SmsPreferences phoneNumber={phoneNumber} isB2B={isB2B} />
              <TelemarketingPreferences phoneNumber={phoneNumber} />
              <MailingPreferences addresses={addresses} />
              <DisclaimerBox />
            </div>
          )}
        </div>
        <SurveyFeedback />
      </div>
    );
  }

  return (
    (shouldTabsRender() ? tabStructureRender() : defaultStructureRender()
    ));
};

export function CommunicationPreferences() {

  return (
    <div
      className="sui-m-auto sui-max-w-screen-2xl sui-bg-primary sui-p-4"
      data-testid={communicationPrefsTestId}
    >
      <Header />
      <div className="sui-grid sui-pt-8 sui-grid-cols-12">
        <div className="sui-col-span-12 lg:sui-col-span-3 sui-pr-8">
          <QueryProvider cacheKey="left-nav">
            <CustomerAccountNavigation />
          </QueryProvider>
        </div>
        <UserDataProvider>
          <RetrieveErrorProvider>
            <Preferences />
          </RetrieveErrorProvider>
        </UserDataProvider>
        <div className="hidden sm:block sm:sui-col-span-1" />
      </div>
    </div>
  );
}

CommunicationPreferences.displayName = 'CommunicationPreferences';
